import { Component, Element, Host, h, Prop, State, Watch } from '@stencil/core';
import { getLocalizedString, replaceTemplateStringsWithValues } from '../../utils/i18n';
import parseJson from '../../utils/parse-json';

import { getAccountUrl, getFeatureFlag, getGlobalSupportPortalUrl, getShouldUseAccountOwdFeatureFlagData, getSupercupFeatureSpec } from '../../utils/header-footer';
import shadowRootParentOrDocument from '../../utils/generic-functions';
import { CamObject, FeatureFlagObject, UserObject } from '../../types/types';

@Component({
  tag: 'aui-footer',
  styleUrl: 'aui-footer.scss',
  shadow: false,
})
export class AuiFooter {
  @Element() element: HTMLAuiFooterElement;

  @Prop({ reflect: true }) cam: CamObject | string;

  @Prop() user: UserObject | string;

  @Prop({ attribute: 'termsurl' }) termsUrl = '';

  @Prop({ attribute: 'supportcasesurl' }) supportCasesUrl;

  @Prop() i18n: Record<string, string> = {};

  @Prop({ attribute: 'shoulduseaccountowd' }) shouldUseAccountOwdOverride?: boolean = false;

  @State() camJson: CamObject = {};

  @State() shouldUseAccountOwd = this.shouldUseAccountOwdOverride;

  @State() userJson: UserObject = {};

  @State() showGlobalSupportPortal = false;

  @Watch('cam')
  watchCam(newValue) {
    const errorMessage = "Please provide valid JSON string on footer's cam attribute";
    const parsedCamJson = parseJson(newValue, errorMessage, {});
    this.camJson = { ...parsedCamJson };
  }

  @Watch('shouldUseAccountOwdOverride')
  watchShouldUseAccountOwd(shouldUseAccountOwdOverride) {
    this.shouldUseAccountOwd = !!shouldUseAccountOwdOverride;
  }

  @Watch('user')
  watchUser(newValue) {
    const errorMessage = "Please provide valid JSON string on footer's user attribute";
    const parsedUserJson = parseJson(newValue, errorMessage, {});
    this.userJson = { ...parsedUserJson };

    if (this.#accountId) {
      getFeatureFlag({
        feature: this.#featureFlagData.showGlobalSupportPortal,
        setFlagValue: flagValue => {
          this.showGlobalSupportPortal = flagValue;
        },
      });
    }
  }

  camPhotoFailedToLoad = false;

  #openDialog() {
    const dialog = shadowRootParentOrDocument(this.element).getElementById('footer-contact-dialog');
    if (dialog.closest('s-popper') || dialog.closest('aui-sidenav')) {
      dialog.remove();
      if (shadowRootParentOrDocument(this.element).body) {
        shadowRootParentOrDocument(this.element).body.appendChild(dialog);
      } else {
        shadowRootParentOrDocument(this.element).appendChild(dialog);
      }
    }
    dialog.setAttribute('open', '');
  }

  static get accountHome() {
    switch (window.aui.env) {
      case 'local':
        return 'https://local.account.avalara.io';
      case 'ci':
        return 'https://ci.account.avalara.io';
      case 'qa':
      case 'uat':
        return 'https://qa.account.avalara.io';
      case 'sbx':
        return 'https://account.avalara.com';
      case 'prod':
      default:
        return 'https://account.avalara.com';
    }
  }

  static get auiFooterText() {
    return {
      'auiFooter.submitSupportCase': 'Submit a support case',
      'auiFooter.contactSupport': 'Contact Support',
      'auiFooter.ariaLabel.contact': 'Open Contact Us dialog',
      'auiFooter.contactButton': 'Contact',
      'auiFooter.contactDialogHeader': 'Contact',
      'auiFooter.termsLink': 'Terms',
      'auiFooter.privacyLink': 'Privacy',
      'auiFooter.ariaLabel.cam': 'Customer Account Manager',
      'auiFooter.camGreeting1': "Hi, I'm {CAM_NAME}.",
      'auiFooter.camGreeting2': 'I can help you with your account and other Avalara resources.',
      'auiFooter.scheduleConversationLink': 'Schedule a conversation',
      'auiFooter.helpCenterLink': 'Knowledge Center',
      'auiFooter.avalaraUniversityLink': 'Avalara University',
    };
  }

  // Prefer accountId if passed, default to delivery system specific ids
  get #accountId() {
    return this.userJson?.accountId || this.userJson?.avataxAccountId || '';
  }

  get #featureFlagData(): FeatureFlagObject {
    return {
      showGlobalSupportPortal: {
        featureSpec: getSupercupFeatureSpec(),
        featureName: 'show-global-support-portal',
        featureData: { avataxAccountId: this.#accountId.toString() },
      },
    };
  }

  get #textReplacement() {
    return {
      camGreeting1: { CAM_NAME: this.camJson.name },
    };
  }

  get #hrefs() {
    const cam = this.camJson;
    const camPhone = cam.phone as string;

    const accountUrl = getAccountUrl(this.shouldUseAccountOwd);
    const loggedInSupportCasesUrl = this.showGlobalSupportPortal ? getGlobalSupportPortalUrl() : `${accountUrl}/goTo/support-cases`;

    return {
      camEmail: this.#isCamValid && cam.email ? `mailto:${cam.email}` : '',
      camPhone: this.#isCamValid && cam.phone ? `tel:${camPhone.replace(/\s/g, '').replace('ext.', 'p')}` : '', // TODO: verify this is formatting correctly and works for international #s once this Jira ticket is resolved: https://jira.avalara.com/browse/IBST-22986
      camScheduling: this.#isCamValid && cam.schedulingUrl ? cam.schedulingUrl : '',
      supportCases: this.#isLoggedIn ? loggedInSupportCasesUrl : 'https://help.avalara.com/Contact_Avalara/Submit_a_Case',
      productsUsage: `${accountUrl}/goTo/products`,
      terms: this.termsUrl || 'https://www.avalara.com/terms',
    };
  }

  get #isCamValid() {
    return this.camJson.isValid;
  }

  get #isLoggedIn() {
    return this.userJson.isLoggedIn;
  }

  #onCamPhotoError() {
    this.camPhotoFailedToLoad = true;
  }

  #getSupportCasesEl() {
    const className = ` flex align-items-center ${this.#isCamValid ? 'margin-top-md' : 'margin-bottom-xs'}`;
    const dataRef = `aui-footer.${this.#isCamValid ? 'cam' : 'default'}-dialog.support-cases`;
    const supportLabelKey = this.showGlobalSupportPortal ? 'auiFooter.contactSupport' : 'auiFooter.submitSupportCase';
    return (
      <a class={className} href={this.#hrefs.supportCases} data-ref={dataRef} target={this.#isLoggedIn && !this.showGlobalSupportPortal ? null : '_blank'}>
        <s-icon name="support" class="pad-right-sm" aria-hidden="true"></s-icon>
        {getLocalizedString(AuiFooter.auiFooterText, this.i18n, supportLabelKey)}
      </a>
    );
  }

  componentWillLoad() {
    // Skip fetching the feature flag if an explicit override attribute is passed
    if (this.shouldUseAccountOwd) return Promise.resolve();
    return getFeatureFlag({
      feature: getShouldUseAccountOwdFeatureFlagData(),
      setFlagValue: flagValue => {
        this.shouldUseAccountOwd = flagValue;
      },
    });
  }

  connectedCallback() {
    this.watchCam(this.cam);
    this.watchUser(this.user);
  }

  render() {
    const { camJson } = this;
    const { camEmail, camPhone, camScheduling } = this.#hrefs;
    const supportCasesEl = this.#getSupportCasesEl();

    return (
      <Host>
        <footer>
          <s-container>
            <span>
              <a href="https://www.avalara.com" data-ref="aui-footer.avalara-dot-com" target="_blank">
                Avalara.com
              </a>
              <button
                type="button"
                class="link"
                onClick={() => this.#openDialog()}
                data-ref="aui-footer.open-contact-dialog"
                aria-label={getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.ariaLabel.contact')}
              >
                {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.contactButton')}
              </button>
              <a href={this.#hrefs.terms} data-ref="aui-footer.terms" target="_blank">
                {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.termsLink')}
              </a>
              <a href="https://www.avalara.com/us/en/legal/privacy-policy.html" data-ref="aui-footer.privacy-policy" target="_blank">
                {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.privacyLink')}
              </a>
            </span>
            <span data-ref="copyright">&copy; Avalara, Inc.</span>
          </s-container>
          {this.#isCamValid ? (
            <s-dialog data-ref="aui-footer.cam-dialog" id="footer-contact-dialog">
              <div slot="header">{getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.contactDialogHeader')}</div>
              <div slot="body">
                <div class="flex pad-bottom-sm">
                  <div data-ref="cam-photo-container">
                    {camJson.photoUrl && !this.camPhotoFailedToLoad ? (
                      <img
                        alt={getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.ariaLabel.cam')}
                        src={camJson.photoUrl as string}
                        class="pad-right-sm"
                        onError={() => this.#onCamPhotoError()}
                      />
                    ) : (
                      <s-icon aria-hidden="true" name="user"></s-icon>
                    )}
                  </div>
                  <div data-ref="cam-text" class="inline-flex flex-dir-col">
                    <div>
                      {replaceTemplateStringsWithValues(getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.camGreeting1'), this.#textReplacement.camGreeting1)}
                    </div>
                    <div>{getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.camGreeting2')}</div>
                  </div>
                </div>
                <div class="flex flex-dir-col">
                  {camJson.schedulingUrl ? (
                    <a class=" flex align-items-center" href={camScheduling as string} data-ref="aui-footer.cam-dialog.schedule" rel="noopener" target="_blank">
                      <s-icon name="calendar" class="pad-right-sm" aria-hidden="true"></s-icon>
                      {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.scheduleConversationLink')}
                    </a>
                  ) : (
                    ''
                  )}
                  <a class="flex align-items-center margin-top-md" href={camEmail} data-ref="aui-footer.cam-dialog.email">
                    <s-icon name="email" class="pad-right-sm" aria-hidden="true"></s-icon>
                    {camJson.email}
                  </a>
                  {camJson.phone ? (
                    <a class=" flex align-items-center margin-top-md" href={camPhone} data-ref="aui-footer.cam-dialog.phone">
                      <s-icon name="phone" class="pad-right-sm" aria-hidden="true"></s-icon>
                      {camJson.phone}
                    </a>
                  ) : (
                    ''
                  )}
                </div>
                <hr />
                <div class="flex flex-dir-col">
                  <a class="flex align-items-center" href="https://knowledge.avalara.com/" data-ref="aui-footer.cam-dialog.help-center" target="_blank">
                    <s-icon name="help-circle" class="pad-right-sm" aria-hidden="true"></s-icon>
                    {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.helpCenterLink')}
                  </a>
                  <a class=" flex align-items-center margin-top-md" href="https://training.avalara.com" data-ref="aui-footer.cam-dialog.avalara-u" target="_blank">
                    <s-icon name="grad-cap" class="pad-right-sm" aria-hidden="true"></s-icon>
                    {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.avalaraUniversityLink')}
                  </a>
                  {supportCasesEl}
                </div>
              </div>
            </s-dialog>
          ) : (
            <s-dialog data-ref="aui-footer.default-dialog" id="footer-contact-dialog">
              <div slot="header">{getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.contactDialogHeader')}</div>
              <div class="flex flex-dir-col" slot="body">
                <a class="margin-bottom-md flex align-items-center" href="https://knowledge.avalara.com/" data-ref="aui-footer.default-dialog.help-center" target="_blank">
                  <s-icon name="help-circle" class="pad-right-sm" aria-hidden="true"></s-icon>
                  {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.helpCenterLink')}
                </a>
                <a class="margin-bottom-md flex align-items-center" href="https://training.avalara.com" data-ref="aui-footer.default-dialog.avalara-u" target="_blank">
                  <s-icon name="grad-cap" class="pad-right-sm" aria-hidden="true"></s-icon>
                  {getLocalizedString(AuiFooter.auiFooterText, this.i18n, 'auiFooter.avalaraUniversityLink')}
                </a>
                {supportCasesEl}
              </div>
            </s-dialog>
          )}
        </footer>
      </Host>
    );
  }
}
